import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { userAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * DisableEnableAccessDialog — confirms disabling or re-enabling a user's
 * portal login. Owns its own submission state and API call. Preserves the
 * agent record; only mutates the linked user's `status`.
 */
export const DisableEnableAccessDialog = ({ open, onOpenChange, linkedUser, onUpdated }) => {
  const [pending, setPending] = React.useState(false);
  const isActive = linkedUser?.status === 'active';

  const handleToggle = async () => {
    if (!linkedUser) return;
    const newStatus = isActive ? 'inactive' : 'active';
    setPending(true);
    try {
      await userAPI.updateStatus(linkedUser.user_id, newStatus);
      toast.success(`User access ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
      onOpenChange(false);
      onUpdated?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update user status'));
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="disable-confirm-title">
            {isActive ? 'Disable User Access?' : 'Enable User Access?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isActive
              ? `This will prevent ${linkedUser?.name || 'this user'} from logging into the portal. Their agent profile and all associated data will remain intact.`
              : `This will restore portal access for ${linkedUser?.name || 'this user'}. They will be able to log in again with their existing credentials.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="disable-confirm-cancel" disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleToggle}
            disabled={pending}
            className={isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
            data-testid="disable-confirm-action"
          >
            {pending ? 'Saving…' : isActive ? 'Disable Access' : 'Enable Access'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DisableEnableAccessDialog;
